// ═══════════════════════════════════════════════════════════════════════════
//  CHART UTILITIES — pure helper functions (no React, no side-effects)
// ═══════════════════════════════════════════════════════════════════════════
import type { Candle, SessionKey } from './ChartDataTypes';
import {
  MARKET_SESSIONS, MCX_AGRI_SYMBOLS, MCX_COMMODITY_SYMBOLS,
  BSE_INDEX_SYMBOLS, NSE_INDEX_SYMBOLS, SYMBOL_REGISTRY, TF_SECONDS,
} from './chartConstants';

// ─── IST formatting ───────────────────────────────────────────────────────────
export const IST_TZ = 'Asia/Kolkata';
export const IST_FMT_DATE = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit' });
export const IST_FMT_TIME = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
export const IST_FMT_FULL = new Intl.DateTimeFormat('en-IN', { timeZone: IST_TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

export const fmtIstDate = (unixSec: number): string => IST_FMT_DATE.format(new Date(unixSec * 1000));
export const fmtIstTime = (unixSec: number): string => IST_FMT_TIME.format(new Date(unixSec * 1000));
export const fmtIstFull = (unixSec: number): string => IST_FMT_FULL.format(new Date(unixSec * 1000));

export const fmtDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

// ─── Symbol helpers ───────────────────────────────────────────────────────────
// An option pane may carry its contract's expiry before the strike ("NIFTY_50 2026-10-27 22500 CE", the ledger's
// contractSymbol form) so every "... <strike> CE" parser keeps working. The API / live-tick name puts it last as
// YYYYMMDD (NIFTY_50_22500_CE_20261027), the spine's own expiry-named spelling (God 2026-10-11 option audit D1/D9).
const _OPT_WITH_EXP = /^(.*?)\s+(\d{4})-(\d{2})-(\d{2})\s+(\d+(?:\.\d+)?)\s+(CE|PE)\s*$/i;
export const toApiSym = (s: string): string => {
  const m = _OPT_WITH_EXP.exec(s || '');
  if (m) return `${toApiSym(m[1])}_${m[5]}_${m[6].toUpperCase()}_${m[2]}${m[3]}${m[4]}`;
  return SYMBOL_REGISTRY[s]?.apiSym || s.replace(/[\s&-]+/g, '_').toUpperCase();
};
const _OPT_ANY = /^(.*?)\s+(?:\d{4}-\d{2}-\d{2}\s+)?(\d+(?:\.\d+)?)\s+(CE|PE)\s*$/i;
/** Underlying API symbol of an option pane ("NIFTY 2026-10-27 22500 CE" -> "NIFTY_50"); '' for any other pane.
 *  God 2026-10-13: the option chart shows the underlying's OI / PCR and RULE #72 frame next to its own premium. */
// option-chain roots (ocOptionBase) -> the underlying's API / data name (OI, RULE #72 cells); MCX roots stay as they are
const _OPT_ROOT_API: Record<string, string> = { NIFTY: 'NIFTY_50', BANKNIFTY: 'BANK_NIFTY', FINNIFTY: 'FIN_NIFTY',
  MIDCPNIFTY: 'MIDCPNIFTY', SENSEX: 'SENSEX', BANKEX: 'BANKEX' };
export const optUnderlying = (s: string): string => {
  const m = _OPT_ANY.exec(s || '');
  if (!m) return '';
  const root = m[1].replace(/[\s&-]+/g, '').toUpperCase();
  return _OPT_ROOT_API[root] || toApiSym(m[1]);
};
export const optExpiryOf = (s: string): string => {
  const m = _OPT_WITH_EXP.exec(s || '');
  return m ? `${m[2]}-${m[3]}-${m[4]}` : '';
};
// "<base> <strike> CE" -> "<base> <YYYY-MM-DD> <strike> CE" (replaces an existing expiry)
export const optWithExpiry = (s: string, exp: string): string => {
  const m = /^(.*?)\s+(?:\d{4}-\d{2}-\d{2}\s+)?(\d+(?:\.\d+)?)\s+(CE|PE)\s*$/i.exec(s || '');
  return m && /^\d{4}-\d{2}-\d{2}$/.test(exp || '') ? `${m[1]} ${exp} ${m[2]} ${m[3].toUpperCase()}` : s;
};

export function isIndexOrOption(sym: string): boolean {
  const api = (sym || '').replace(/[\s&-]+/g, '_').toUpperCase();
  return NSE_INDEX_SYMBOLS.has(api) || BSE_INDEX_SYMBOLS.has(api) || /\s+(CE|PE)\s*$/i.test(sym);
}

export function defaultTfForSym(sym: string): string {
  return isIndexOrOption(sym) ? '5s' : '1m';
}

export function getSessionForSymbol(apiSym: string): SessionKey {
  const s = (apiSym || '').toUpperCase();
  if (MCX_AGRI_SYMBOLS.has(s)) return 'MCX_AGRI';
  for (const c of MCX_COMMODITY_SYMBOLS) if (s === c || s.startsWith(c + '_')) return 'MCX';
  if (BSE_INDEX_SYMBOLS.has(s)) return 'BSE';
  return 'NSE';
}

export function _istClock(d: Date): { mins: number; day: number } {
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  return { mins: ist.getUTCHours() * 60 + ist.getUTCMinutes(), day: ist.getUTCDay() };
}

export function isMarketOpen(d: Date = new Date(), symbol?: string): boolean {
  const key: SessionKey = symbol ? getSessionForSymbol(toApiSym(symbol)) : 'NSE';
  const { mins, day } = _istClock(d);
  if (day === 0 || day === 6) return false;
  const s = MARKET_SESSIONS[key];
  return mins >= s.open && mins < s.close;
}

// ─── Data cleaning ────────────────────────────────────────────────────────────
export function prepareChartData(candles: Candle[]): Candle[] {
  if (!candles.length) return candles;
  candles.sort((a, b) => a.time - b.time);
  const out: Candle[] = [];
  for (const c of candles) {
    const last = out[out.length - 1];
    if (last && last.time === c.time) {
      last.high   = Math.max(last.high,   c.high);
      last.low    = Math.min(last.low,    c.low);
      last.close  = c.close;
      last.volume += c.volume;
    } else {
      out.push({ ...c });
    }
  }
  return out;
}

export function resampleCandles(src: Candle[], targetTfSec: number): Candle[] {
  if (!src.length || !targetTfSec || targetTfSec <= 0) return [];
  const IST_OFF = 19800;
  const out: Candle[] = [];
  let cur: Candle | null = null;
  let curBucket = -1;
  for (const c of src) {
    const bucket = Math.floor((c.time + IST_OFF) / targetTfSec) * targetTfSec - IST_OFF;
    if (bucket !== curBucket) {
      if (cur) out.push(cur);
      cur = { time: bucket, open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume };
      curBucket = bucket;
    } else if (cur) {
      cur.high = Math.max(cur.high, c.high);
      cur.low  = Math.min(cur.low,  c.low);
      cur.close = c.close;
      cur.volume += c.volume;
    }
  }
  if (cur) out.push(cur);
  return out;
}

export function robustPriceRange(candles: Candle[], start: number, end: number, skip?: Set<number>): { minP: number; maxP: number } {
  // EMERGENCY REVERT (God/Subhas 2026-07-22, 3PM): the percentile-clip range
  // CLIPPED the extreme-move candles OUT of [min,max] during 3PM volatility, so
  // those candle bodies rendered past the pane edge as huge solid blocks (priceToY
  // does not clamp). Back to naive min/max of low/high, which ALWAYS contains every
  // visible candle → proper OHLC bodies+wicks, never a block. (Vertical-squish
  // handling to be re-done later by clamping candles to the pane, not clipping the
  // range.)
  // `skip` (God 2026-10-31 chart-brain Q1): bar indexes that LABEL but never SCALE - the index closing-auction print
  // (ChartCanvas casMemo). A view holding only skipped bars falls back to the plain range so it never goes blank.
  let mn = Infinity, mx = -Infinity, lastKept = -1;
  const skipping = !!(skip && skip.size);
  for (let i = start; i <= end; i++) {
    const c = candles[i];
    if (!c) continue;
    if (skipping && skip!.has(i)) continue;
    lastKept = i;
    if (c.low > 0 && c.low < mn) mn = c.low;
    if (c.high > 0 && c.high > mx) mx = c.high;
  }
  if (mn === Infinity || mx <= 0) return skipping ? robustPriceRange(candles, start, end) : { minP: 0, maxP: 1 };
  // Keep the latest close inside the scale so the live price label is never clipped (the latest NON-skipped bar).
  const lastIdx = Math.min(end, candles.length - 1);
  const lastClose = (skipping && skip!.has(lastIdx) && lastKept >= 0 ? candles[lastKept] : candles[lastIdx])?.close;
  if (lastClose && lastClose > 0) { if (lastClose < mn) mn = lastClose; if (lastClose > mx) mx = lastClose; }
  const spread = mx - mn;
  const pad = spread > 0 ? spread * 0.05 : Math.max(mx, 1) * 0.0005;
  return { minP: mn - pad, maxP: mx + pad };
}

// ─── Bad-tick sanitiser ─────────────────────────────────────────────────────────
// One corrupt candle (zero/near-zero price, or a runaway wick) renders as a
// "crash" spike AND blows out the auto price-scale. Compare each bar to a LOCAL
// median (works for intraday and multi-year alike): drop fully-broken bars and
// clamp a single runaway wick back onto the bar's body.
export function sanitizeCandles(candles: Candle[]): Candle[] {
  const n = candles.length;
  if (n < 5) return candles;
  const W = 7;
  const out: Candle[] = [];
  for (let i = 0; i < n; i++) {
    const c = candles[i];
    if (!(c.open > 0 && c.high > 0 && c.low > 0 && c.close > 0)) continue;   // drop broken bar
    const a = Math.max(0, i - W), b = Math.min(n - 1, i + W);
    const win: number[] = [];
    for (let j = a; j <= b; j++) { if (j === i) continue; const v = candles[j].close; if (v > 0) win.push(v); }
    if (win.length < 3) { out.push(c); continue; }
    win.sort((x, y) => x - y);
    const med = win[Math.floor(win.length / 2)];
    if (med <= 0) { out.push(c); continue; }
    // Body wildly off the local median → clear bad tick, skip the bar entirely.
    if (c.close < med * 0.6 || c.close > med * 1.6 || c.open < med * 0.6 || c.open > med * 1.6) continue;
    // Clamp a runaway wick (>15% past the local median) back to the body.
    let { open, high, low, close } = c;
    const bodyHi = Math.max(open, close), bodyLo = Math.min(open, close);
    if (low  < med * 0.85) low  = bodyLo;
    if (high > med * 1.15) high = bodyHi;
    out.push({ ...c, open, high, low, close });
  }
  return out.length >= Math.floor(n * 0.4) ? out : candles;
}


// ─── OPTION SERIES FRESHNESS — ONE LAW, IMPORTED BY EVERY CALLER ─────────────
// An option pane must never draw a series that has STOPPED updating. A strike
// that falls out of the captured ATM ladder keeps its last bar forward-filled,
// so it charts as a flat lie sitting next to a live index.
//
// MEASURED (2026-09-17, live option series during market hours):
//   1m newest bar 246 s old   5m 735 s   15m 1941 s
// i.e. the option candle builder trails by TWO bars plus a ~135 s constant, so
// the rule is three bars plus five minutes, floored at 600 (which leaves
// 1s/5s/15s/1m byte-identical to the original constant).
//
// This lived inline in ChartPane in three places. It is here because
// SuperChart/index.tsx must apply the SAME test when it decides which strike a
// pane should follow - two copies of this rule drifting apart is how a pane
// ends up pinned to a contract the chart itself would refuse to draw.
export const optStaleCutoffSec = (tf: string): number =>
  Math.max(600, 3 * (TF_SECONDS[tf] || 60) + 300);

// LEDGER OUTCOME (God 2026-09-24): what an exit WAS, read from the ledger's own
// exitReason - never from the sign of the P&L. A trailed stop that ended green
// is still an SL; a booked target that ended red after costs is still PROFIT.
// The same table lives in Algo-Engine/tools/verify_ledger_markers.py, which
// proves the served bundle and the ledger agree row for row.
export type LedgerOutcome = 'SL' | 'PROFIT' | 'EOD' | 'EXIT';
export function ledgerOutcome(reason: any): LedgerOutcome {
  const r = String(reason || '').toUpperCase();
  if (r.includes('SL') && !r.includes('BOOK')) return 'SL';
  if (/BOOK|TARGET|TRAIL-LOCK|PROFIT|TGT/.test(r)) return 'PROFIT';
  if (/EOD|MARKET_CLOSED|SQUARE/.test(r)) return 'EOD';
  return 'EXIT';
}

// Unix seconds of TODAY's session open for this symbol's own exchange (IST).
export function sessionOpenTodaySec(symbol: string, now: Date = new Date()): number {
  const key: SessionKey = getSessionForSymbol(toApiSym(symbol));
  const s = MARKET_SESSIONS[key];
  const dayStartIST = Math.floor((now.getTime() / 1000 + 19800) / 86400) * 86400 - 19800;
  return dayStartIST + s.open * 60;
}

// Is this series too old to be shown AS LIVE?
// Only meaningful while the symbol's OWN market is open: after the close a last
// bar stamped 15:29 is the correct final bar of the day, not a fault.
//
// SESSION-AWARE, NOT AGE-BASED (God 2026-09-23 night, "instant back-candles").
// The old test rejected the WHOLE history whenever the newest bar was older
// than ~600 s while the market was open. Measured on 2026-09-23: the spine
// went dark 52 times (5-10 min each, one per restart cycle), and at every one
// of them every option pane threw its history away, printed "back-candles
// retry N/12 - empty response" and started from the current moment - which is
// exactly the fault God reported. The only history that must be refused is a
// PREVIOUS session's flat-fill (a strike not captured today, served as
// yesterday's 15:29 bars): that is decided by the SESSION the newest bar
// belongs to, never by how many minutes ago it printed. A quiet strike or a
// feed blackout keeps its bars; the live feed appends when it returns.
export function isOptSeriesStale(
  newestTs: number,
  tf: string,
  symbol: string,
  now: Date = new Date(),
): boolean {
  if (!newestTs) return true;
  if (!isMarketOpen(now, symbol)) return false;
  const open = sessionOpenTodaySec(symbol, now);
  // give the feed one cutoff after the bell before calling yesterday's bars stale
  if (now.getTime() / 1000 - open < optStaleCutoffSec(tf)) return false;
  return newestTs < open;
}
